import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import MouseTrail from '../components/MouseTrail';
import TextScramble from '../components/TextScramble';
import { aboutAPI } from '../services/api';
import { useScrollReveal } from '../hooks/useScrollReveal';
import GlowCard from '../components/GlowCard';
import { useTheme } from '../context/ThemeContext';

const ScrollRevealSection = ({ children, className = '', direction = 'up' }) => {
  const ref = useScrollReveal({ threshold: 0.1, triggerOnce: true });
  const animClass = direction === 'left' ? 'scroll-reveal-left' : 
                    direction === 'right' ? 'scroll-reveal-right' : 
                    'scroll-reveal';
  
  return (
    <div ref={ref} className={`${animClass} ${className}`}>
      {children}
    </div>
  );
};

const About = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const fetchAboutContent = async () => {
      try {
        const response = await aboutAPI.get();
        setContent(response.data);
      } catch (error) {
        console.error('Failed to fetch about content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutContent();
  }, []);

  if (loading) {
    return (
      <div className={`relative min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <div className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Loading...</div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className={`relative min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'
      }`}>
        <div className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Content not available</div>
      </div>
    );
  }

  const glowColors = isDarkMode 
    ? ['rgba(138, 100, 255, 0.6)', 'rgba(100, 200, 255, 0.6)', 'rgba(255, 150, 100, 0.6)', 'rgba(100, 255, 180, 0.6)']
    : ['rgba(99, 102, 241, 0.3)', 'rgba(56, 189, 248, 0.3)', 'rgba(251, 146, 60, 0.3)', 'rgba(34, 197, 94, 0.3)'];

  return (
    <div className={`relative min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <MouseTrail />
      <Navigation />
      
      <main className="relative z-10 pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-8">
          <ScrollRevealSection className="mb-20">
            <h1 className={`text-6xl md:text-8xl font-light leading-tight mb-6 tracking-tight ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <TextScramble as="span" speed={30}>{content.title}</TextScramble>
            </h1>
            <p className={`text-xl md:text-2xl font-light leading-relaxed ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <TextScramble as="span" speed={20}>{content.subtitle}</TextScramble>
            </p>
          </ScrollRevealSection>

          <ScrollRevealSection className="mb-20">
            <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <TextScramble as="span" speed={20}>{content.description}</TextScramble>
            </p>
          </ScrollRevealSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {content.stats.map((stat, index) => (
              <ScrollRevealSection 
                key={index}
                direction={index % 2 === 0 ? 'left' : 'right'}
              >
                <GlowCard 
                  className={`border backdrop-blur-sm transition-all duration-500 group ${
                    isDarkMode 
                      ? 'border-white/10 bg-black/30' 
                      : 'border-gray-200 bg-white/80 shadow-md'
                  }`}
                  glowColor={glowColors[index % glowColors.length]}
                  glowSize={200}
                >
                  <div className="p-6">
                    <div className={`text-4xl md:text-5xl font-light mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'group-hover:text-white' : 'text-gray-900'
                    }`}>
                      {stat.value}
                    </div>
                    <div className={`text-sm uppercase tracking-wider ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {stat.label}
                    </div>
                  </div>
                </GlowCard>
              </ScrollRevealSection>
            ))}
          </div>

          <div className="space-y-16">
            {content.sections.map((section, index) => (
              <ScrollRevealSection 
                key={section.id}
                direction={index % 2 === 0 ? 'left' : 'right'}
              >
                <div className={`border-l pl-8 transition-all duration-500 group ${
                  isDarkMode 
                    ? 'border-white/20 hover:border-white/60' 
                    : 'border-gray-300 hover:border-gray-500'
                }`}>
                  <h2 className={`text-3xl md:text-4xl font-light mb-4 transition-colors duration-300 ${
                    isDarkMode ? 'group-hover:text-white' : 'text-gray-900'
                  }`}>
                    <TextScramble as="span" speed={30}>{section.title}</TextScramble>
                  </h2>
                  <p className={`leading-relaxed transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-600 group-hover:text-gray-700'
                  }`}>
                    <TextScramble as="span" speed={20}>{section.content}</TextScramble>
                  </p>
                </div>
              </ScrollRevealSection>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;
